import { Button, InputField, InputText } from "@sellar-io/components";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { Alert } from "../../../../../components/alert/Alert";
import {
  Dialog,
  DialogContent,
  DialogCTA,
  DialogNavigation,
} from "../../../../../components/modal/Dialog";
import { LAYOUT_GROUPING_GROUP } from "../../../../../constants/routes";
import { apiV2AssertError } from "../../../../../hooks/api/v2/apiV2Request";
import { ServerStateKeys } from "../../../../../hooks/api/v2/ServerStateKeys";
import { useProductList } from "../../../../../hooks/api/v2/product/useProductList";
import { useStorefrontTagCreate } from "../../../../../hooks/api/v2/storefrontTags/useStorefrontTagCreate";
import { useFirebaseUser } from "../../../../../hooks/useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../../../hooks/useSellarCurrentBusiness.shared";
import { ProductSelection } from "./ProductSelection";

export const CreateGroupDialog = ({ onDismiss }: { onDismiss: () => void }) => {
  const [name, setName] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const mutation = useStorefrontTagCreate();
  const queryClient = useQueryClient();
  const { user } = useFirebaseUser();
  const { business } = useSellarCurrentBusiness();
  const navigate = useNavigate();
  const products = useProductList({
    sortBy: "name",
    sortDir: "asc",
    query: [
      { type: "Products", modifier: "NOT_NULL" },
      { type: "showAllProducts", modifier: "EQUALS", value: true },
      { type: "active", modifier: "EQUALS", value: true },
    ],
  });
  const dismiss = () => {
    if (!mutation.isPending) onDismiss();
  };
  return (
    <Dialog active onDismiss={dismiss}>
      <DialogNavigation
        title="Create new group"
        description="Select which products from your whole range you want to include in your new group."
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={dismiss}
      />
      <DialogContent className="flex flex-col gap-4 p-4">
        {mutation.isError && (
          <Alert
            type="error"
            message={
              apiV2AssertError(mutation.error)?.response?.data.message ??
              "The group could not be created. Try again."
            }
          />
        )}
        <InputField
          label="Name"
          note="Your customers will see this group's name on your storefront"
        >
          <InputText
            autoFocus
            placeholder="Type a name..."
            value={name}
            disabled={mutation.isPending}
            onChange={(event) => setName(event.target.value)}
          />
        </InputField>
        {products.isLoading ? (
          <p>Loading products…</p>
        ) : products.isError ? (
          <Alert type="error" message="Products could not be loaded.">
            <Button onClick={() => void products.refetch()}>Try again</Button>
          </Alert>
        ) : (
          <ProductSelection
            products={products.data?.pages.flat() ?? []}
            selectedIds={selectedIds}
            onChange={setSelectedIds}
            disabled={mutation.isPending}
          />
        )}
        {products.hasNextPage && (
          <Button
            color="transparent"
            loading={products.isFetchingNextPage}
            onClick={() => void products.fetchNextPage()}
          >
            Load more products
          </Button>
        )}
      </DialogContent>
      <DialogCTA>
        <Button
          loading={mutation.isPending}
          disabled={
            mutation.isPending ||
            !name.trim() ||
            selectedIds.length === 0 ||
            products.isError
          }
          onClick={() =>
            mutation.mutate(
              { value: name.trim(), ProductIds: selectedIds },
              {
                onSuccess: async (group) => {
                  await Promise.all([
                    queryClient.invalidateQueries({
                      queryKey: [business?.id, ServerStateKeys.Products],
                    }),
                    queryClient.invalidateQueries({
                      queryKey: [
                        user?.uid,
                        business?.id,
                        ServerStateKeys.UngroupedProducts,
                      ],
                    }),
                  ]);
                  navigate(LAYOUT_GROUPING_GROUP.href({ groupId: group.id }));
                  onDismiss();
                },
              },
            )
          }
        >
          Create group
        </Button>
      </DialogCTA>
    </Dialog>
  );
};
